/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get */
import Component from '@ember/component';
import { action } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tagName } from '@ember-decorators/component';
import { cached, tracked } from '@glimmer/tracking';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';

@tagName('')
export default class GroupGoalSetter extends Component {
  // Services
  @service
  features;

  @service
  session;

  @service
  store;

  @service
  toaster;

  GoalState = GoalState;
  isPeakTime = false;
  @tracked
  saveInProgress = false;
  onClose() {}

  @or('goal.subject.isGradable', 'goal.subject.isTopic')
  canGrade;

  @tracked
  nextStepsDialogOpen;

  @tracked
  selectedPerson;

  // The states that @states gives. Callers give a store.query Promise, which
  // has no array methods, so the component keeps the resolved array here.
  // It is null until the load completes.
  @tracked
  loadedStates = null;

  #statesSource;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let source = this.states;
    if (source === this.#statesSource) {
      return;
    }
    this.#statesSource = source;
    this.loadedStates = null;
    Promise.resolve(source).then((states) => {
      // Ignore a load that a newer @states replaced.
      if (source === this.#statesSource && !this.isDestroying) {
        this.loadedStates = states ?? [];
      }
    });
  }

  @cached
  get statesMap() {
    return (this.loadedStates ?? []).reduce((map, state) => {
      map[state.person_id] = state;
      return map;
    }, {});
  }

  @action
  close() {
    this.onClose();
  }

  @action
  noOp() {}

  @action
  refreshStates() {
    this.updateStates();
  }

  @action
  stateClass(state) {
    if (isEmpty(state)) {
      return 'goal-not-set';
    }
    switch (state.get('awarded')) {
      case GoalState.DEVELOPING:
        return 'goal-developing';
      case GoalState.SECURE:
        return 'goal-secure';
    }
    return 'goal-not-set';
  }

  @action
  toggleGoalState(person, state) {
    let awarded = GoalState.DEVELOPING;
    this.saveInProgress = true;
    if (!isEmpty(state)) {
      switch (state.get('awarded')) {
        case GoalState.DEVELOPING:
          awarded = GoalState.SECURE;
          break;
        case GoalState.SECURE:
          awarded = GoalState.NOT_SET;
          break;
      }
    }

    let stateParams = {
      schoolId: this.get('session.school'),
      assessorId: this.get('session.person'),
      personId: person,
      goalId: this.goal,
      awarded,
    };

    if (!isEmpty(state) && !isEmpty(state.get('grade'))) {
      stateParams.grade = state.get('grade'); // Access grade directly
    }

    let newState = this.store.createRecord('goals/state', stateParams);

    saveOrDiscard(newState)
      .then(() => {
        this.updateStates();
      })
      .catch(() => this.toaster.error('Error saving the goal state.'));
  }

  @action
  openNextSteps(person) {
    this.selectedPerson = person;
    this.nextStepsDialogOpen = true;
  }

  @action
  closeNextSteps() {
    this.updateStates();
    this.nextStepsDialogOpen = false;
  }

  @action
  markAll(awarded) {
    // Do not save before the states load: the existing grades would be lost.
    if (!this.loadedStates) {
      return;
    }
    this.saveInProgress = true;
    this.group.currentLearners
      .reduce((previousPromise, nextLearner) => {
        if (
          !isEmpty(this.statesMap) &&
          !isEmpty(this.statesMap[nextLearner.person.id]) &&
          this.statesMap[nextLearner.person.id].awarded === awarded
        ) {
          return previousPromise;
        }
        return previousPromise.then(() => {
          let stateParams = {
            schoolId: this.get('session.school'),
            assessorId: this.get('session.person'),
            personId: nextLearner.person,
            goalId: this.goal,
            awarded,
          };
          let state = this.statesMap[nextLearner.person.id];
          if (!isEmpty(state) && !isEmpty(state.get('grade'))) {
            stateParams.grade = state.get('grade');
          }

          let newState = this.store.createRecord('goals/state', stateParams);

          return saveOrDiscard(newState);
        });
      }, Promise.resolve())
      .then(() => {
        let label = GoalState.getLabel(awarded);
        if (GoalState.DEVELOPING === awarded) {
          label = 'Current';
        }
        this.toaster.success(`Goal marked as ${label}.`);
      })
      .catch(() => {
        this.toaster.error(`Error updating a Goal State. Please try again.`);
      })
      .finally(() => {
        this.saveInProgress = false;
        this.updateStates();
      });
  }

  updateStates() {
    this.saveInProgress = false;
    this.loadedStates?.update();
  }
}
